import React, {MouseEvent, useContext, useEffect, useMemo, useState} from 'react';
import {Button, CircularProgress, Divider, Popover} from '@mui/material';
import {Logout, Search} from '@mui/icons-material';
import {COLORS, SIZES, Typography} from '@fupa/fupa-uikit';
import {styled} from '@mui/material/styles';
import {UserContext} from '../../auth/userContext';
import useSignOut from '../../auth/useSignOut';
import {auth} from '../../service/firebase';
import {UndecoratedATag} from '../link/Link';
import {SnackbarSeverity, useSnackbar} from '../snackbar/snackbar';
import Avatar from './Avatar';
import {UserRole} from '@fupa/fupa-ts-types';

const HIGHLIGHT_COLOR = '#00518f';

const Action = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AuthAction = styled('div')`
  display: flex;
  justify-content: flex-end;
  width: 4.5rem;
`;

const Container = styled('header')`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SIZES['16']} ${SIZES['32']};
  background: ${COLORS.fupaPrimary};

  background: radial-gradient(
    circle farthest-corner at center center,
    ${HIGHLIGHT_COLOR} 0%,
    ${COLORS.fupaPrimary} 60%
  );
  background: -webkit-radial-gradient(
    circle farthest-corner at center center,
    ${HIGHLIGHT_COLOR} 0%,
    ${COLORS.fupaPrimary} 60%
  );
  background: -moz-radial-gradient(
    circle farthest-corner at center center,
    ${HIGHLIGHT_COLOR} 0%,
    ${COLORS.fupaPrimary} 60%
  );
  width: 100%;
  z-index: 2;
  @media all and (max-width: 800px) {
    padding: ${SIZES['16']} ${SIZES['16']};
  }
`;

const SettingsItem = styled('div')`
  display: flex;
  align-items: center;
  padding: ${SIZES['8']} ${SIZES['16']};
  cursor: pointer;
`;

const Logo = styled('img')<{}>`
  width: auto;
  cursor: pointer;
  height: ${SIZES['56']};
  @media all and (max-width: 800px) {
    height: ${SIZES['42']};
  }
`;

const AppBar = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const user = useContext(UserContext);
  const [signOut, loading, error] = useSignOut(auth);
  const {snackbarState, setSnackbarState, Snackbar, closeSnackbar} = useSnackbar();

  const isAdmin = useMemo(() => user?.fupaUser?.r?.includes(UserRole.Admin) ?? false, [user]);

  useEffect(() => {
    if (error) {
      setSnackbarState({
        open: true,
        message: 'Wir konnten dich nicht abmelden. Bitte versuche es erneut.',
        severity: SnackbarSeverity.ERROR,
      });
    } else {
      closeSnackbar();
    }
  }, [error, closeSnackbar, setSnackbarState]);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  return (
    <Container>
      <UndecoratedATag href='/'>
        <Logo src={'/FuPaLogo.svg'} alt='Zur Startseite' />
      </UndecoratedATag>
      {user?.fupaUser ? (
        <Action>
          <UndecoratedATag href={`/builder/#/overview`} margin>
            <Button variant='contained' size='small'>
              Zum Widget-Builder
            </Button>
          </UndecoratedATag>
          <AuthAction>
            <Avatar fupaUser={user.fupaUser} onClick={handleClick} />
          </AuthAction>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}>
            {isAdmin ? (
              <UndecoratedATag href={`/builder/#/admin`}>
                <SettingsItem>
                  <Search sx={{color: COLORS.darkGrey, marginRight: SIZES['8']}} />
                  <Typography variant='body1'>Alle Widgets durchsuchen</Typography>
                </SettingsItem>
              </UndecoratedATag>
            ) : null}
            <Divider />
            <SettingsItem onClick={handleLogout}>
              {loading ? (
                <CircularProgress sx={{color: COLORS.darkGrey, marginRight: SIZES['8']}} size={24} />
              ) : (
                <Logout sx={{color: COLORS.darkGrey, marginRight: SIZES['8']}} />
              )}
              <Typography variant='body1'>Abmelden</Typography>
            </SettingsItem>
          </Popover>
        </Action>
      ) : (
        <div>
          <UndecoratedATag href={`/builder`} margin>
            <Button variant='contained' size='small'>
              Zum Widget-Builder
            </Button>
          </UndecoratedATag>
          <UndecoratedATag href={`/auth/login?redirectUrl=${encodeURIComponent('/builder/#/overview')}`}>
            <Button sx={{width: '4.5rem'}} variant='contained' size='small'>
              Anmelden
            </Button>
          </UndecoratedATag>
        </div>
      )}
      <Snackbar
        open={snackbarState.open}
        message={snackbarState.message}
        severity={snackbarState.severity}
        onClose={closeSnackbar}
        autoHideDuration={4000}
      />
    </Container>
  );
};

export default AppBar;
